import { useEffect, useState } from "react";
import api, { openPdf, downloadCsv } from "../lib/api";
import PageHeader from "../components/PageHeader";
import { Button } from "../components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { AlertTriangle, ShoppingCart, RefreshCcw, FileText, Download, Clock } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

export default function ReorderSuggestions() {
  const [data, setData] = useState({ groups: [], unassigned: [] });
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(null);
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/reorder-suggestions");
      setData(data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const createOrder = async (group) => {
    if (!window.confirm(`Bestellung an "${group.supplier_name}" mit ${group.items.length} Artikel(n) erstellen?`)) return;
    setCreating(group.supplier_id);
    try {
      const { data } = await api.post(`/reorder-suggestions/create/${group.supplier_id}`);
      toast.success(`Bestellung ${data.number} erstellt`);
      load();
      setTimeout(() => navigate("/einkauf"), 500);
    } catch (err) { toast.error(err.response?.data?.detail || "Fehler"); }
    finally { setCreating(null); }
  };

  return (
    <div data-testid="page-reorder">
      <PageHeader
        eyebrow="Einkauf"
        title="Bestellvorschläge"
        description="Alle Artikel unter Min-Bestand, gruppiert nach Lieferant. Ein Klick erstellt die Bestellung."
        actions={
          <div className="flex items-center gap-2 flex-wrap">
            <Button onClick={() => openPdf("/reorder-suggestions.pdf")} variant="outline"
                    data-testid="btn-reorder-pdf" className="rounded-full">
              <FileText size={14} className="mr-1" /> PDF
            </Button>
            <Button onClick={() => downloadCsv("/reorder-suggestions.csv", "bestellvorschlag.csv")}
                    variant="outline" data-testid="btn-reorder-csv" className="rounded-full">
              <Download size={14} className="mr-1" /> CSV
            </Button>
            <Button onClick={load} variant="outline" data-testid="btn-reorder-refresh" className="rounded-full">
              <RefreshCcw size={14} className="mr-1" /> Aktualisieren
            </Button>
          </div>
        }
      />

      {loading && <div className="text-[var(--text-muted)]">Lade…</div>}

      {!loading && data.groups.length === 0 && data.unassigned.length === 0 && (
        <div className="bg-white border border-[var(--border)] rounded-2xl p-10 text-center">
          <div className="inline-flex w-12 h-12 rounded-full bg-[#d1fae5] text-[#065f46] items-center justify-center mb-3">✓</div>
          <div className="font-display text-xl font-bold mb-1">Alles im grünen Bereich!</div>
          <div className="text-[var(--text-muted)] text-sm">Kein Artikel liegt aktuell unter dem Min-Bestand.</div>
        </div>
      )}

      {data.groups.map((g) => (
        <div key={g.supplier_id} data-testid={`reorder-group-${g.supplier_id}`}
             className="bg-white border border-[var(--border)] rounded-2xl overflow-hidden mb-4">
          <div className="flex items-center justify-between p-4 border-b border-[var(--border)] bg-[var(--pop-cream)]/40 flex-wrap gap-3">
            <div>
              <div className="font-display text-lg font-bold">{g.supplier_name}</div>
              <div className="text-xs text-[var(--text-muted)] flex items-center gap-3 flex-wrap">
                <span>{g.items.length} Artikel unter Min-Bestand</span>
                <span className="inline-flex items-center gap-1"><Clock size={12} /> Lieferzeit {g.lead_time_days ?? 7} Tage</span>
                {g.supplier_email && <span className="font-mono">{g.supplier_email}</span>}
              </div>
            </div>
            <Button onClick={() => createOrder(g)} disabled={creating === g.supplier_id}
                    data-testid={`btn-create-po-${g.supplier_id}`}
                    className="rounded-full bg-[var(--pop-yellow)] text-black hover:bg-[var(--pop-yellow-dark)] font-bold">
              <ShoppingCart size={16} className="mr-1" />
              {creating === g.supplier_id ? "Erstelle…" : "Bestellung erstellen"}
            </Button>
          </div>
          <Table className="table-zebra">
            <TableHeader>
              <TableRow>
                <TableHead className="overline">SKU</TableHead>
                <TableHead className="overline">Artikel</TableHead>
                <TableHead className="overline text-right">Bestand</TableHead>
                <TableHead className="overline text-right">Min</TableHead>
                <TableHead className="overline text-right">Ø / Tag</TableHead>
                <TableHead className="overline text-right">Reicht</TableHead>
                <TableHead className="overline text-right">Vorschlag</TableHead>
                <TableHead className="overline text-right">Preis (€)</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {g.items.map((it) => (
                <TableRow key={it.item_id}>
                  <TableCell className="font-mono text-xs">{it.sku}</TableCell>
                  <TableCell>{it.name}</TableCell>
                  <TableCell className="text-right font-mono text-xs text-[var(--danger)]">{it.stock} {it.unit}</TableCell>
                  <TableCell className="text-right font-mono text-xs">{it.min_stock} {it.unit}</TableCell>
                  <TableCell className="text-right font-mono text-xs">{it.avg_daily_usage ?? 0}</TableCell>
                  <TableCell className="text-right font-mono text-xs">
                    {it.days_left == null ? "—" : (
                      <span className={it.days_left <= 0 ? "text-[var(--danger)] font-bold" : it.days_left < 3 ? "text-orange-600" : ""}>
                        {it.days_left} T
                      </span>
                    )}
                  </TableCell>
                  <TableCell className="text-right font-mono text-sm font-bold">{it.suggested_qty} {it.unit}</TableCell>
                  <TableCell className="text-right font-mono text-xs">{it.price.toFixed(2)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      ))}

      {data.unassigned.length > 0 && (
        <div className="bg-white border border-[var(--border)] rounded-2xl overflow-hidden">
          <div className="flex items-center gap-2 p-4 border-b border-[var(--border)] bg-[#fef3c7]">
            <AlertTriangle size={18} className="text-[#854d0e]" />
            <div>
              <div className="font-semibold text-[#854d0e]">Ohne Lieferant ({data.unassigned.length})</div>
              <div className="text-xs text-[#92400e]">Diese Artikel brauchen eine Lieferanten-Zuordnung, bevor sie automatisch bestellt werden können.</div>
            </div>
          </div>
          <Table className="table-zebra">
            <TableHeader>
              <TableRow>
                <TableHead className="overline">SKU</TableHead>
                <TableHead className="overline">Artikel</TableHead>
                <TableHead className="overline text-right">Bestand</TableHead>
                <TableHead className="overline text-right">Min</TableHead>
                <TableHead className="overline text-right">Ø / Tag</TableHead>
                <TableHead className="overline text-right">Reicht</TableHead>
                <TableHead className="overline text-right">Vorschlag</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.unassigned.map((it) => (
                <TableRow key={it.item_id} data-testid={`unassigned-row-${it.item_id}`}>
                  <TableCell className="font-mono text-xs">{it.sku}</TableCell>
                  <TableCell>{it.name}</TableCell>
                  <TableCell className="text-right font-mono text-xs text-[var(--danger)]">{it.stock} {it.unit}</TableCell>
                  <TableCell className="text-right font-mono text-xs">{it.min_stock} {it.unit}</TableCell>
                  <TableCell className="text-right font-mono text-xs">{it.avg_daily_usage ?? 0}</TableCell>
                  <TableCell className="text-right font-mono text-xs">{it.days_left == null ? "—" : `${it.days_left} T`}</TableCell>
                  <TableCell className="text-right font-mono text-sm font-bold">{it.suggested_qty} {it.unit}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
